import WastedCalculator from "../components/marketing/WastedCalculator";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Brain } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * Standalone landing page for the Wasted AI Visibility Calculator.
 *
 * Acts as a cold-traffic SEO magnet for queries like
 * "kalkulačka straty AI" or "ai search lost revenue".
 *
 * Conversion flow:
 *   1. User lands → instantly sees ~big loss number (auto-computed)
 *   2. Adjusts inputs to their own business → number grows
 *   3. Hits "Vidieť detailný AI audit za €9" CTA → routes to /snapshot
 */
export default function WastedCalculatorPage() {
  return (
    <main data-testid="wasted-calculator-page" className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Kalkulačka stratených konverzií • TopRankPulse</title>
        <meta
          name="description"
          content="Koľko stratíš mesačne kvôli zlej AI viditeľnosti? Vypočítaj presné číslo za 30 sekúnd."
        />
      </Helmet>

      <header className="max-w-5xl mx-auto px-6 pt-16 pb-10 text-center">
        <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold mb-4">
          Bezplatný 2026 nástroj
        </span>
        <h1
          data-testid="wasted-page-title"
          className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-4 leading-tight"
          style={{ textWrap: "balance" }}
        >
          Kalkulačka stratených konverzií
          <br />
          <span className="text-red-600">z nízkej AI viditeľnosti</span>
        </h1>
        <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto">
          ChatGPT, Perplexity a Gemini si delia <strong>35 % buyer-research dopytov v 2026</strong>.
          Ak ťa AI necituje, strácaš zákazníkov ktorí už nikdy nepôjdu cez Google.
        </p>
      </header>

      <section className="max-w-5xl mx-auto px-6 pb-12">
        <WastedCalculator />
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16 grid grid-cols-1 md:grid-cols-3 gap-4">
        <FeatureCard
          icon={<ShieldCheck className="w-5 h-5 text-green-600" />}
          title="Reálne dáta"
          body="Vzorec vychádza z verejne dostupných 2026 reportov o AI search share."
        />
        <FeatureCard
          icon={<Brain className="w-5 h-5 text-purple-600" />}
          title="Auto-doplnenie skóre"
          body="Ak si u nás už skenoval doménu, AI viditeľnosť sa doplní automaticky."
        />
        <FeatureCard
          icon={<BarChart3 className="w-5 h-5 text-blue-600" />}
          title="Mesačné + ročné číslo"
          body="Krátky breakdown vzorca, takže si môžeš overiť každý krok."
        />
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">
            Strácaš peniaze. Tu je, kde presne.
          </h2>
          <p className="text-sm text-gray-300 max-w-xl mx-auto mb-5">
            Za €9 dostaneš detailný AI Snapshot — ktoré AI vyhľadávače ťa
            citujú, pri ktorých queries a čo opraviť aby si stratu zastavil.
          </p>
          <Link
            to="/snapshot"
            data-testid="wasted-cta-snapshot"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 text-gray-900 rounded-lg text-sm font-bold hover:bg-amber-300 transition-colors"
          >
            Spustiť AI Snapshot za €9
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-2">{icon}</div>
      <h3 className="text-base font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
    </div>
  );
}
